import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { flattenEntityProposals, toAiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiProposalForm";
import { parseAiNamedOption, parseAiString } from "src/components/formFields/ai/utils/bindAiProposal";
import {
  AiFieldBinding,
  aiFieldBinding,
  reconcilePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialAttributeDimensionFragment,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import type { MaterialListing_DetailFragment } from "src/routes/libraries/material-catalog/components/materialListingDetail.generated";
import type { MaterialImportAiProposal_ReviewFragment as MaterialImportAiProposal } from "src/routes/libraries/material-catalog/material-import/MaterialImportPage.generated";
import { HasIdAndName, fail, toSentence } from "src/utils";
import { ObjectState } from "src/utils/formState";
import { MaterialListingFormState, MaterialVariantFormValue } from "./materialListingForm";

/**
 * Marks every still-pending proposal Accepted, i.e. "the user reviewed this and left it alone".
 *
 * Without this they stay Pending forever: `resolveAiProposalStatus` only fires on the fields the
 * user actually edits, and nothing else resolves the rest.
 */
export function acceptRemainingProposals(
  formState: MaterialListingFormState,
  opts: { items: MaterialCatalog_ItemFragment[]; brands: HasIdAndName[] },
): void {
  const { items, brands } = opts;
  const dimensions = items.find((i) => i.id === formState.itemId.value)?.materialAttributeDimensions ?? [];
  reconcilePendingAiProposals(
    [...listingBindings(formState, brands), ...formState.variants.rows.flatMap((variant) => variantBindings(variant))],
    AiFieldProposalStatus.Accepted,
  );
  // Dimensions can't be bindings: a dimension's proposal names a MAV whose id lives in the variant's
  // `mavIds` list.
  formState.variants.rows.forEach((variant) => acceptDimensionProposals(variant, dimensions));
  // Anything still pending has no form field, so it must be accepted here or the import never resolves.
  // We can only do this safely for two cases: a CREATE, whose value is already on the row, and `imageUrl`,
  // whose asset was downloaded server-side. Otherwise fail loudly, so we don't accept proposals the user
  // couldn't actually see and doesn't know they're accepting.
  allProposals(formState)
    .filter((proposal) => proposal.status.value === AiFieldProposalStatus.Pending)
    .forEach((proposal) => {
      if (proposal.parentType.value !== AiEntityProposalType.Create && proposal.fieldName.value !== "imageUrl") {
        fail(`No form field to apply the ${proposal.fieldName.value} proposal to`);
      }
      proposal.status.set(AiFieldProposalStatus.Accepted);
    });
}

function allProposals(formState: MaterialListingFormState) {
  return [...formState.fieldProposals.rows, ...formState.variants.rows.flatMap((v) => v.fieldProposals.rows)];
}

/** The listing's own AI-bound fields, paired with the parser each one's select or input uses. */
function listingBindings(formState: MaterialListingFormState, brands: HasIdAndName[]): AiFieldBinding[] {
  const proposal = (fieldName: string) => formState.fieldProposals.rows.find((p) => p.fieldName.value === fieldName);
  return [
    aiFieldBinding(formState.name, parseAiString, proposal("name")),
    aiFieldBinding(formState.description, parseAiString, proposal("description")),
    aiFieldBinding(formState.designNote, parseAiString, proposal("designNote")),
    // The agent stores the brand's name; the field is the brand's id.
    aiFieldBinding(formState.brandId, parseAiNamedOption(brands), proposal("brand")),
  ];
}

/** One variant's AI-bound fields. Its dimensions are handled separately. */
function variantBindings(variant: ObjectState<MaterialVariantFormValue>): AiFieldBinding[] {
  const proposal = (fieldName: string) => variant.fieldProposals.rows.find((p) => p.fieldName.value === fieldName);
  return [
    aiFieldBinding(variant.modelNumber, parseAiString, proposal("modelNumber")),
    aiFieldBinding(variant.manufacturerUrl, parseAiString, proposal("manufacturerUrl")),
  ];
}

/**
 * Swaps in the proposed MAV for each dimension the agent reported, leaving the other dimensions alone.
 *
 * Reads `mavIds` fresh per dimension so two dimensions accepted in the same sweep see each other's
 * writes, rather than both starting from the list as it was before the sweep.
 */
function acceptDimensionProposals(
  variant: ObjectState<MaterialVariantFormValue>,
  dimensions: MaterialCatalog_MaterialAttributeDimensionFragment[],
): void {
  for (const dimension of dimensions) {
    const proposal = variant.fieldProposals.rows.find((p) => p.fieldName.value === dimension.name);
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) continue;
    // The same name -> id lookup the dimension's own select does.
    const options = (dimension.values ?? []).map((mav) => ({ id: mav.id, name: mav.textValue }));
    const proposedMavId = parseAiNamedOption(options)(proposal.fieldValue.value);
    if (proposedMavId === undefined) {
      proposal.status.set(AiFieldProposalStatus.Rejected);
      continue;
    }
    const currentMavIds = variant.mavIds.value ?? [];
    const replacedMavId = options.find((o) => currentMavIds.includes(o.id))?.id;
    // Already the selected value, so accepting it is agreement, not an edit
    if (proposedMavId !== replacedMavId) {
      variant.mavIds.set([...currentMavIds.filter((id) => id !== replacedMavId), proposedMavId]);
    }
    proposal.status.set(AiFieldProposalStatus.Accepted);
  }
}

/**
 * The listing the import created, from its one `MATERIAL_LISTING` entity proposal.
 * Imports apply their creates optimistically, so a finished proposal already points at real rows.
 */
export function importedListingId(proposal: MaterialImportAiProposal | undefined): string | undefined {
  for (const { entity } of proposal?.entityProposals ?? []) {
    if (entity?.__typename === "MaterialListing") return entity.id;
  }
  return undefined;
}

/**
 * The agent's proposals for the listing's own fields, flattened for the form.
 */
export function listingFieldProposals(proposal: MaterialImportAiProposal | undefined): AiFieldProposalFormValue[] {
  return flattenEntityProposals(
    (proposal?.entityProposals ?? []).filter((aep) => aep.entity?.__typename === "MaterialListing"),
  );
}

export type VariantFieldProposals = Record<string, AiFieldProposalFormValue[]>;

/**
 * The agent's proposals for each imported variant's own fields, keyed by variant id.
 */
export function variantFieldProposals(proposal: MaterialImportAiProposal | undefined): VariantFieldProposals {
  const byVariantId: VariantFieldProposals = {};
  for (const { entity, fieldProposals } of proposal?.entityProposals ?? []) {
    if (entity?.__typename !== "MaterialVariant") continue;
    byVariantId[entity.id] = fieldProposals.map((fp) => toAiFieldProposalFormValue(fp));
  }
  return byVariantId;
}

/** How the import summary names each listing field the agent can propose. */
const listingFieldNouns: Record<string, string> = {
  name: "the name",
  description: "a description",
  brand: "the brand",
  designNote: "design notes",
};

/**
 * One sentence on what an import into an existing listing found, for the review banner, i.e.
 * "Blueprint AI captured a change to the name, added a description, and added 1 additional variant."
 */
export function materialImportSummary(
  listing: Pick<MaterialListing_DetailFragment, "name" | "description" | "brand" | "designNote">,
  listingProposals: AiFieldProposalFormValue[],
  variantProposals: VariantFieldProposals,
): string | undefined {
  // What the listing says today for each field the agent can propose, keyed the way the proposals name them
  const current: Record<string, string | null | undefined> = {
    name: listing.name,
    description: listing.description,
    brand: listing.brand?.name,
    designNote: listing.designNote,
  };
  const [added, changed] = listingProposals
    .filter((p) => listingFieldNouns[p.fieldName])
    .partition((p) => !current[p.fieldName]);
  const variants = Object.values(variantProposals).filter((ps) => ps.nonEmpty);
  const [created, updated] = variants.partition((ps) => ps.some((p) => p.parentType === AiEntityProposalType.Create));
  const clauses = [
    changed.nonEmpty
      ? `captured a change to ${toSentence(changed.map((p) => listingFieldNouns[p.fieldName]))}`
      : undefined,
    added.nonEmpty ? `added ${toSentence(added.map((p) => listingFieldNouns[p.fieldName]))}` : undefined,
    updated.nonEmpty ? `updated ${pluralizeVariants(updated.length, "")}` : undefined,
    created.nonEmpty ? `added ${pluralizeVariants(created.length, "additional ")}` : undefined,
  ].compact();
  if (clauses.isEmpty) return undefined;
  return `Blueprint AI ${toSentence(clauses)}.`;
}

function pluralizeVariants(count: number, adjective: string): string {
  return `${count} ${adjective}variant${count === 1 ? "" : "s"}`;
}
